<script>
import AppLayout from '@/layouts/AppLayout.vue'
import { ref, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { CheckCircleIcon, UserIcon, EnvelopeIcon, ChatBubbleLeftRightIcon } from '@heroicons/vue/20/solid'
import { playStoreDeleteDataFn } from '@/services/api/restApiCalls'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useRouter } from 'vue-router'

export default {
  components: {
    CheckCircleIcon,
    AppLayout,
    UserIcon,
    EnvelopeIcon,
    ChatBubbleLeftRightIcon
  },
  setup() {
    // const authStore = useAuthGlobalStore()
    const restoStore = useRestoGLobalStore()
    const router = useRouter()

    const name = ref('')
    const emailId = ref('')
    const message = ref('')
    const submitted = ref(false)
    const deleteDataInfo = ref({})

    const validationRules = computed(() => {
      let rules = {
        name: { required },
        emailId: {
          required,
          email
        },
        message: { required }
      }
      return rules
    })
    const v$ = useVuelidate(validationRules, {
      name,
      emailId,
      message
    })
    const handlePlayStoreDeleteData = async () => {
      submitted.value = true
      v$.value.$validate()
      if (v$.value.$invalid) {
        return
      }
      const currentDate = new Date().toISOString().split('T')[0] //'YYYY-MM-DD' format
      const playStoreDeleteDataCaptcha = `beluga-playstore-deletedata-request-${currentDate}`
      const base64EncodedValue = btoa(playStoreDeleteDataCaptcha) //Encoded signupCaptcha
      const playStoreDeleteFormData = new FormData()
      playStoreDeleteFormData.append('name', name.value)
      playStoreDeleteFormData.append('email', emailId.value)
      playStoreDeleteFormData.append('message', message.value)
      playStoreDeleteFormData.append('beluga_captcha', base64EncodedValue)
      const delete_data_info = await playStoreDeleteDataFn(
        restoStore?.RESTO_ID,
        playStoreDeleteFormData
      )
      deleteDataInfo.value = delete_data_info
    }

    return {
      name,
      emailId,
      message,
      submitted,
      deleteDataInfo,
      v$,
      router,
      restoStore,
      handlePlayStoreDeleteData
    }
  }
}
</script>
<template>
  <AppLayout>
    <div class="flex flex-col items-center justify-center sm:px-6 px-3 sm:py-8 py-4 mx-auto">
      <div class="w-full max-w-xl p-6 sm:p-8 bg-white dark:bg-gray-800 rounded-lg shadow transition-colors duration-200">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-gray-100 text-center mb-1 transition-colors duration-200">Apricot IT LTD</h2>
        <p class="text-sm text-center mb-4 text-gray-600 dark:text-gray-400 transition-colors duration-200">Developer Name: OnlyGulp, Inc.</p>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-6 transition-colors duration-200">
          {{ restoStore?.restoInfo?.name }} Mobile App
        </h2>
        <div v-if="deleteDataInfo?.status == 200" class="text-center">
          <CheckCircleIcon class="h-14 w-14 text-green-500 dark:text-green-400 mx-auto" aria-hidden="true" />
          <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-300 my-1 transition-colors duration-200">
            We have received your request to delete data. Thank you!
          </h3>
        </div>
        <div v-else class="">
          <h2 class="text-lg font-normal text-gray-900 dark:text-gray-100 text-center mb-6 transition-colors duration-200">
            Please fill this form to submit request to delete your data
          </h2>
          <div class="">
            <form class="space-y-6" @submit.prevent="handlePlayStoreDeleteData">
              <div class="md:grid md:grid-cols-12 md:gap-x-6">
                <div class="md:col-span-12">
                  <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-200">Name</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    </div>
                    <input
                      id="name"
                      v-model="name"
                      name="name"
                      type="text"
                      autocomplete="name"
                      placeholder="Enter name"
                      :class="{ 'border-red-600 dark:border-red-500 focus:border-red-600 focus:ring-red-200 dark:focus:ring-red-800/20': submitted && v$.name.$error }"
                      class="block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-700 shadow-sm focus:border-violet-500 focus:ring focus:ring-violet-200 dark:focus:ring-violet-800/20 focus:ring-opacity-50 pl-10 px-4 py-3 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    />
                  </div>
                  <div v-for="(error, index) of v$.name.$errors" :key="index" class="mt-1">
                    <div class="text-sm font-medium text-red-600 dark:text-red-400 transition-colors duration-200">{{ error.$message }}</div>
                  </div>
                </div>
              </div>

              <div class="md:grid md:grid-cols-12 md:gap-x-6">
                <div class="md:col-span-12">
                  <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-200">Email address</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    </div>
                    <input
                      id="email"
                      v-model="emailId"
                      name="email"
                      type="email"
                      autocomplete="email"
                      placeholder="Enter email"
                      :class="{ 'border-red-600 dark:border-red-500 focus:border-red-600 focus:ring-red-200 dark:focus:ring-red-800/20': submitted && v$.emailId.$error }"
                      class="block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-700 shadow-sm focus:border-violet-500 focus:ring focus:ring-violet-200 dark:focus:ring-violet-800/20 focus:ring-opacity-50 pl-10 px-4 py-3 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    />
                  </div>
                  <div v-for="(error, index) of v$.emailId.$errors" :key="index" class="mt-1">
                    <div class="text-sm font-medium text-red-600 dark:text-red-400 transition-colors duration-200">{{ error.$message }}</div>
                  </div>
                </div>
              </div>

              <div class="md:grid md:grid-cols-12 md:gap-x-6">
                <div class="md:col-span-12">
                  <label for="message" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-200">Message</label>
                  <div class="relative">
                    <div class="absolute top-3 left-3 pointer-events-none">
                      <ChatBubbleLeftRightIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    </div>
                    <textarea
                      id="message"
                      v-model="message"
                      name="message"
                      autocomplete="message"
                      placeholder="Write something here..."
                      :class="{ 'border-red-600 dark:border-red-500 focus:border-red-600 focus:ring-red-200 dark:focus:ring-red-800/20': submitted && v$.message.$error }"
                      class="block w-full appearance-none rounded-lg border border-gray-300 dark:border-gray-700 shadow-sm focus:border-violet-500 focus:ring focus:ring-violet-200 dark:focus:ring-violet-800/20 focus:ring-opacity-50 pl-10 px-4 py-3 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 min-h-[120px]"
                    ></textarea>
                  </div>
                  <div v-for="(error, index) of v$.message.$errors" :key="index" class="mt-1">
                    <div class="text-sm font-medium text-red-600 dark:text-red-400 transition-colors duration-200">{{ error.$message }}</div>
                  </div>
                </div>
              </div>

              <div class="flex justify-center space-x-4">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-lg border border-transparent bg-gray-500 py-3 px-6 text-sm font-medium text-white shadow-sm hover:bg-gray-600 dark:hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
                  @click="$router.push({ name: 'restoHome' })"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="inline-flex justify-center rounded-lg border border-transparent bg-violet-700 py-3 px-6 text-sm font-medium text-white shadow-sm hover:bg-violet-800 dark:hover:bg-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
